import { useEffect, useState, useMemo } from "react";
import { getLeads, updateLeadStatus, deleteLead } from "../../lib/api";
import { toast } from "sonner";
import { Trash2, Search, ChevronLeft, ChevronRight, ChevronDown, Mail, Phone, MessageSquare, Calendar, Briefcase, User, X } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../ui/alert-dialog";

const STATUSES = ["new", "contacted", "qualified", "closed"];
const PAGE_SIZES = [10, 20, 50, 200];

const STATUS_LABELS = {
  new: "Novo",
  contacted: "Contactado",
  qualified: "Qualificado",
  closed: "Fechado",
};

const STATUS_COLORS = {
  new: "bg-amber-50 text-amber-700 border-amber-200",
  contacted: "bg-blue-50 text-blue-700 border-blue-200",
  qualified: "bg-emerald-50 text-emerald-700 border-emerald-200",
  closed: "bg-gray-50 text-gray-600 border-gray-200",
};

const STATUS_DOT = {
  new: "bg-amber-500",
  contacted: "bg-blue-500",
  qualified: "bg-emerald-500",
  closed: "bg-gray-400",
};

function timeAgo(date) {
  const now = new Date();
  const then = new Date(date);
  const diff = Math.floor((now - then) / 1000);
  if (diff < 60) return "agora";
  if (diff < 3600) return `${Math.floor(diff / 60)}min`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d`;
  return then.toLocaleDateString("pt-PT", { day: "2-digit", month: "short" });
}

export default function AdminLeads() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  async function load() {
    setLoading(true);
    try {
      const data = await getLeads();
      setItems(data || []);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    let result = items;
    if (statusFilter !== "all") {
      result = result.filter((l) => l.status === statusFilter);
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(
        (l) =>
          l.name?.toLowerCase().includes(q) ||
          l.email?.toLowerCase().includes(q) ||
          l.phone?.toLowerCase().includes(q) ||
          l.service?.toLowerCase().includes(q) ||
          l.message?.toLowerCase().includes(q)
      );
    }
    return result;
  }, [items, search, statusFilter]);

  const totalPages = Math.ceil(filtered.length / pageSize);
  const paginated = filtered.slice((page - 1) * pageSize, page * pageSize);

  useEffect(() => { setPage(1); }, [search, pageSize, statusFilter]);

  const statusCounts = useMemo(() => {
    const counts = { all: items.length };
    STATUSES.forEach((s) => { counts[s] = items.filter((l) => l.status === s).length; });
    return counts;
  }, [items]);

  async function handleUpdateStatus(id, status) {
    try {
      await updateLeadStatus(id, status);
      toast.success("Status atualizado.");
      load();
      if (selected?.id === id) setSelected((prev) => ({ ...prev, status }));
    } catch (err) { console.error(err); toast.error("Falha ao atualizar."); }
  }

  async function remove() {
    if (!deleting) return;
    try {
      setItems((prev) => prev.filter((l) => l.id !== deleting));
      if (selected?.id === deleting) setSelected(null);
      setDeleting(null);
      await deleteLead(deleting);
      toast.success("Lead removido.");
    } catch (err) {
      console.error(err);
      toast.error("Falha ao excluir.");
      load();
    }
  }

  return (
    <div data-testid="admin-leads-tab" className="grid lg:grid-cols-12 gap-8">
      {/* Left Column - Table */}
      <div className="lg:col-span-7 xl:col-span-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="font-serif text-3xl text-softblack">Leads</h2>
            <p className="text-sm text-warmgray mt-1">{items.length} leads no total</p>
          </div>
        </div>

        {/* Status Filter Pills */}
        <div className="flex flex-wrap gap-1.5 md:gap-2 mb-4">
          <button
            onClick={() => setStatusFilter("all")}
            className={`flex items-center gap-1 md:gap-2 px-2.5 md:px-4 py-1.5 md:py-2 text-[10px] md:text-xs tracking-wider uppercase rounded-full border transition-all whitespace-nowrap ${
              statusFilter === "all" ? "bg-softblack text-white border-softblack" : "bg-white text-warmgray border-fendi hover:border-champagne"
            }`}
          >
            Todos <span className="text-[9px] md:text-[10px] opacity-60">{statusCounts.all}</span>
          </button>
          {STATUSES.map((s) => (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              className={`flex items-center gap-1 md:gap-2 px-2.5 md:px-4 py-1.5 md:py-2 text-[10px] md:text-xs tracking-wider uppercase rounded-full border transition-all whitespace-nowrap ${
                statusFilter === s ? "bg-softblack text-white border-softblack" : "bg-white text-warmgray border-fendi hover:border-champagne"
              }`}
            >
              <span className={`w-1 h-1 md:w-1.5 md:h-1.5 rounded-full ${STATUS_DOT[s]}`} />
              {STATUS_LABELS[s]} <span className="text-[9px] md:text-[10px] opacity-60">{statusCounts[s] || 0}</span>
            </button>
          ))}
        </div>

        {/* Search & Pagination Controls */}
        <div className="bg-white border border-fendi rounded-xl p-3 md:p-4 mb-3 md:mb-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 md:gap-4">
          <div className="relative w-full sm:w-72">
            <Search size={14} className="absolute left-2.5 md:left-3 top-1/2 -translate-y-1/2 text-warmgray/60" strokeWidth={1.5} />
            <input
              type="text"
              placeholder="Buscar leads..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="editorial-field pl-8 md:pl-9 !py-1.5 md:!py-2 text-xs md:text-sm"
            />
            {search && (
              <button onClick={() => setSearch("")} className="absolute right-2.5 md:right-3 top-1/2 -translate-y-1/2 text-warmgray hover:text-softblack">
                <X size={12} />
              </button>
            )}
          </div>
          <div className="flex items-center gap-2 md:gap-3">
            <span className="eyebrow text-[10px] md:text-xs">Mostrar</span>
            <select
              value={pageSize}
              onChange={(e) => setPageSize(Number(e.target.value))}
              className="editorial-field !py-1.5 md:!py-2 text-xs md:text-sm bg-transparent w-16 md:w-20 text-center"
            >
              {PAGE_SIZES.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Mobile: Card List */}
        <div className="lg:hidden space-y-3">
          {loading && (
            <div className="bg-white border border-fendi/60 rounded-xl py-12 text-center text-warmgray text-sm flex items-center justify-center gap-2">
              <div className="w-4 h-4 border-2 border-champagne border-t-transparent rounded-full animate-spin" />
              Carregando…
            </div>
          )}
          {!loading && paginated.length === 0 && (
            <div className="bg-white border border-fendi/60 rounded-xl py-12 text-center">
              <Search size={20} className="mx-auto mb-3 text-warmgray/30" strokeWidth={1} />
              <p className="text-warmgray font-light text-sm">Nenhum lead encontrado.</p>
            </div>
          )}
          {paginated.map((l) => (
            <div key={l.id} data-testid={`lead-row-${l.id}`} className={`bg-white border rounded-xl overflow-hidden transition-all ${
              selected?.id === l.id ? "border-champagne shadow-sm" : "border-fendi/60 shadow-[0_1px_2px_rgba(0,0,0,0.02)]"
            }`}>
              <button
                onClick={() => setSelected(selected?.id === l.id ? null : l)}
                className="w-full flex items-center gap-3 p-3.5 text-left"
              >
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-fendi/30 to-fendi/10 flex items-center justify-center text-xs font-medium text-softblack shrink-0">
                  {l.name?.charAt(0).toUpperCase() || "?"}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-sm font-semibold text-softblack truncate">{l.name}</p>
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[9px] tracking-wider uppercase rounded-full border shrink-0 ${STATUS_COLORS[l.status] || STATUS_COLORS.new}`}>
                      <span className={`w-1 h-1 rounded-full ${STATUS_DOT[l.status]}`} />
                      {STATUS_LABELS[l.status] || l.status}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="text-[11px] text-warmgray/70 truncate">{l.email}</span>
                    <span className="text-[10px] text-warmgray/40">·</span>
                    <span className="text-[10px] text-warmgray/50 shrink-0">{timeAgo(l.created_at)}</span>
                  </div>
                </div>
              </button>

              {selected?.id === l.id && (
                <div className="border-t border-champagne/20 bg-champagne/[0.02] animate-in slide-in-from-top-1 duration-200">
                  <div className="px-3.5 py-3 space-y-2.5">
                    {l.service && (
                      <div className="flex items-center gap-2.5 text-xs text-warmgray/80">
                        <Briefcase size={13} className="shrink-0 text-champagne" strokeWidth={1.5} />
                        <span className="font-medium text-softblack/90">{l.service}</span>
                      </div>
                    )}
                    <a href={`mailto:${l.email}`} className="flex items-center gap-2.5 text-xs text-warmgray/80 hover:text-softblack transition-colors">
                      <Mail size={13} className="shrink-0 text-champagne" strokeWidth={1.5} />
                      <span className="truncate text-softblack/80">{l.email}</span>
                    </a>
                    <a href={`tel:${l.phone}`} className="flex items-center gap-2.5 text-xs text-warmgray/80 hover:text-softblack transition-colors">
                      <Phone size={13} className="shrink-0 text-champagne" strokeWidth={1.5} />
                      <span className="truncate text-softblack/80">{l.phone}</span>
                    </a>
                    {l.contact_method && (
                      <div className="flex items-center gap-2.5 text-xs text-warmgray/80">
                        <MessageSquare size={13} className="shrink-0 text-champagne" strokeWidth={1.5} />
                        <span>Prefere <span className="text-softblack/90">{l.contact_method}</span></span>
                      </div>
                    )}
                    {l.message && (
                      <div className="mt-2 bg-white/70 rounded-lg p-3 border border-fendi/20">
                        <p className="text-[9px] uppercase tracking-wider text-warmgray/60 mb-1.5 font-medium">Mensagem</p>
                        <p className="text-xs text-softblack/80 leading-relaxed">{l.message}</p>
                      </div>
                    )}
                  </div>
                  <div className="px-3.5 py-3 bg-fendi/[0.03] border-t border-fendi/10 flex items-center justify-between">
                    <div className="flex flex-wrap gap-1.5">
                      {STATUSES.map((s) => (
                        <button
                          key={s}
                          data-testid={`lead-status-${s}`}
                          onClick={() => { setSelected(l); handleUpdateStatus(l.id, s); }}
                          className={`px-2.5 py-1 text-[9px] tracking-wider uppercase rounded-full border transition-all ${
                            l.status === s
                              ? "bg-softblack text-white border-softblack"
                              : "bg-white text-warmgray border-fendi/30 hover:border-champagne"
                          }`}
                        >
                          {STATUS_LABELS[s]}
                        </button>
                      ))}
                    </div>
                    <button
                      data-testid={`lead-delete-${l.id}`}
                      onClick={(e) => { e.stopPropagation(); setDeleting(l.id); }}
                      className="text-warmgray/40 hover:text-destructive transition-colors p-1.5 shrink-0 ml-2"
                    >
                      <Trash2 size={14} strokeWidth={1.5} />
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Desktop: Table */}
        <div className="hidden lg:block bg-white border border-fendi rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left border-b border-fendi/80 text-warmgray bg-fendi/5">
                <th className="px-5 py-3.5 eyebrow !text-[10px]">Nome</th>
                <th className="px-5 py-3.5 eyebrow !text-[10px] hidden sm:table-cell">Serviço</th>
                <th className="px-5 py-3.5 eyebrow !text-[10px]">Status</th>
                <th className="px-5 py-3.5 eyebrow !text-[10px] hidden md:table-cell">Recebido</th>
                <th className="w-10" />
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={5} className="px-5 py-16 text-center text-warmgray">
                  <div className="flex items-center justify-center gap-2">
                    <div className="w-4 h-4 border-2 border-champagne border-t-transparent rounded-full animate-spin" />
                    Carregando…
                  </div>
                </td></tr>
              )}
              {!loading && paginated.length === 0 && (
                <tr><td colSpan={5} className="px-5 py-16 text-center">
                  <Search size={24} className="mx-auto mb-3 text-warmgray/30" strokeWidth={1} />
                  <p className="text-warmgray font-light">Nenhum lead encontrado.</p>
                </td></tr>
              )}
              {paginated.map((l) => (
                <tr
                  key={l.id}
                  data-testid={`lead-row-${l.id}`}
                  className={`border-b border-fendi/30 cursor-pointer transition-all ${
                    selected?.id === l.id ? "bg-fendi/15" : "hover:bg-fendi/5"
                  }`}
                  onClick={() => setSelected(l)}
                >
                  <td className="px-5 py-3.5">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-fendi/20 flex items-center justify-center text-xs font-medium text-softblack flex-shrink-0">
                        {l.name?.charAt(0).toUpperCase() || "?"}
                      </div>
                      <div className="min-w-0">
                        <p className="text-softblack font-medium truncate">{l.name}</p>
                        <p className="text-warmgray text-xs truncate max-w-[160px]">{l.email}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-5 py-3.5 text-warmgray hidden sm:table-cell">
                    <div className="flex items-center gap-1.5">
                      <Briefcase size={12} className="text-warmgray/50 flex-shrink-0" />
                      <span className="truncate">{l.service}</span>
                    </div>
                  </td>
                  <td className="px-5 py-3.5">
                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-[10px] tracking-wider uppercase rounded-full border ${STATUS_COLORS[l.status] || STATUS_COLORS.new}`}>
                      <span className={`w-1 h-1 rounded-full ${STATUS_DOT[l.status]}`} />
                      {STATUS_LABELS[l.status] || l.status}
                    </span>
                  </td>
                  <td className="px-5 py-3.5 hidden md:table-cell">
                    <div className="flex items-center gap-1.5 text-warmgray text-xs">
                      <Calendar size={12} className="text-warmgray/50" />
                      {timeAgo(l.created_at)}
                    </div>
                  </td>
                  <td className="px-5 py-3.5 text-right">
                    <button
                      data-testid={`lead-delete-${l.id}`}
                      onClick={(e) => { e.stopPropagation(); setDeleting(l.id); }}
                      className="text-warmgray/40 hover:text-destructive transition-colors p-1.5 rounded-lg hover:bg-red-50"
                    >
                      <Trash2 size={15} strokeWidth={1.5} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination Footer */}
        {!loading && filtered.length > 0 && (
          <div className="bg-white border border-fendi rounded-xl mt-4 p-3 md:p-4 flex flex-col sm:flex-row items-center justify-between gap-3 md:gap-4">
            <p className="text-xs md:text-sm text-warmgray">
              {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, filtered.length)} de {filtered.length}
            </p>
            <div className="flex items-center gap-1 md:gap-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="w-8 h-8 md:w-9 md:h-9 flex items-center justify-center border border-fendi rounded-lg hover:bg-fendi/20 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronLeft size={14} />
              </button>
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) pageNum = i + 1;
                else if (page <= 3) pageNum = i + 1;
                else if (page >= totalPages - 2) pageNum = totalPages - 4 + i;
                else pageNum = page - 2 + i;
                return (
                  <button
                    key={pageNum}
                    onClick={() => setPage(pageNum)}
                    className={`w-7 h-7 md:w-9 md:h-9 flex items-center justify-center rounded-lg text-[11px] md:text-sm transition-colors ${
                      page === pageNum ? "bg-softblack text-white" : "border border-fendi hover:bg-fendi/20"
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              })}
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="w-8 h-8 md:w-9 md:h-9 flex items-center justify-center border border-fendi rounded-lg hover:bg-fendi/20 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronRight size={14} />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Desktop Detail Panel */}
      <div className="hidden lg:block lg:col-span-5 xl:col-span-4">
        {selected ? (
          <div data-testid="lead-detail" className="bg-white border border-fendi rounded-xl overflow-hidden sticky top-24">
            {/* Header */}
            <div className="bg-gradient-to-br from-fendi/10 to-fendi/5 p-6 pb-5 border-b border-fendi/30">
              <div className="flex items-start justify-between mb-4">
                <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-[10px] tracking-wider uppercase rounded-full border ${STATUS_COLORS[selected.status] || STATUS_COLORS.new}`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${STATUS_DOT[selected.status]}`} />
                  {STATUS_LABELS[selected.status] || selected.status}
                </span>
                <span className="text-[10px] text-warmgray">{timeAgo(selected.created_at)}</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-white border border-fendi/30 flex items-center justify-center text-lg font-serif text-softblack">
                  {selected.name?.charAt(0).toUpperCase() || "?"}
                </div>
                <div>
                  <h3 className="font-serif text-xl text-softblack">{selected.name}</h3>
                  <p className="text-xs text-warmgray mt-0.5">{selected.service}</p>
                </div>
              </div>
            </div>

            {/* Contact Info */}
            <div className="p-6 space-y-4">
              <a href={`mailto:${selected.email}`} className="flex items-center gap-3 p-3 rounded-lg hover:bg-fendi/10 transition-colors group">
                <div className="w-9 h-9 rounded-lg bg-fendi/15 flex items-center justify-center flex-shrink-0 group-hover:bg-champagne/20 transition-colors">
                  <Mail size={16} className="text-champagne" strokeWidth={1.5} />
                </div>
                <div className="min-w-0">
                  <p className="eyebrow text-[10px] text-warmgray">Email</p>
                  <p className="text-sm text-softblack truncate group-hover:text-champagne transition-colors">{selected.email}</p>
                </div>
              </a>
              <a href={`tel:${selected.phone}`} className="flex items-center gap-3 p-3 rounded-lg hover:bg-fendi/10 transition-colors group">
                <div className="w-9 h-9 rounded-lg bg-fendi/15 flex items-center justify-center flex-shrink-0 group-hover:bg-champagne/20 transition-colors">
                  <Phone size={16} className="text-champagne" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="eyebrow text-[10px] text-warmgray">Telefone</p>
                  <p className="text-sm text-softblack group-hover:text-champagne transition-colors">{selected.phone}</p>
                </div>
              </a>
              {selected.contact_method && (
                <div className="flex items-center gap-3 p-3 rounded-lg bg-fendi/5">
                  <div className="w-9 h-9 rounded-lg bg-fendi/15 flex items-center justify-center flex-shrink-0">
                    <MessageSquare size={16} className="text-champagne" strokeWidth={1.5} />
                  </div>
                  <div>
                    <p className="eyebrow text-[10px] text-warmgray">Prefere</p>
                    <p className="text-sm text-softblack">{selected.contact_method}</p>
                  </div>
                </div>
              )}
              {selected.message && (
                <div className="p-4 rounded-lg bg-fendi/5 border border-fendi/20">
                  <p className="eyebrow text-[10px] text-warmgray mb-2">Mensagem</p>
                  <p className="text-sm text-softblack font-light leading-relaxed">{selected.message}</p>
                </div>
              )}
              <div className="flex items-center gap-2 text-xs text-warmgray pt-2">
                <Calendar size={13} className="text-warmgray/50" />
                {new Date(selected.created_at).toLocaleString("pt-PT", { day: "2-digit", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" })}
              </div>
            </div>

            {/* Status Update */}
            <div className="p-6 border-t border-fendi/30 bg-fendi/5">
              <p className="eyebrow mb-3 text-[10px]">Atualizar status</p>
              <div className="flex flex-wrap gap-2">
                {STATUSES.map((s) => (
                  <button
                    key={s}
                    data-testid={`lead-status-${s}`}
                    onClick={() => handleUpdateStatus(selected.id, s)}
                    className={`px-3 py-1.5 text-[10px] tracking-wider uppercase rounded-full border transition-all ${
                      selected.status === s
                        ? "bg-softblack text-white border-softblack"
                        : "bg-white text-warmgray border-fendi/40 hover:border-champagne"
                    }`}
                  >
                    {STATUS_LABELS[s]}
                  </button>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <div className="bg-white border border-fendi/60 rounded-xl p-10 text-warmgray text-sm font-light text-center sticky top-24">
            <User size={24} className="mx-auto mb-3 text-warmgray/30" strokeWidth={1} />
            <p>Selecione um lead para ver os detalhes.</p>
          </div>
        )}
      </div>

      <AlertDialog open={!!deleting} onOpenChange={() => setDeleting(null)}>
        <AlertDialogContent className="bg-white border border-fendi rounded-xl">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif text-3xl text-softblack">Excluir Lead?</AlertDialogTitle>
            <AlertDialogDescription className="text-warmgray text-base font-light leading-relaxed">
              Esta ação removerá permanentemente este lead. Você não poderá recuperar estes dados.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="mt-10 gap-4">
            <AlertDialogCancel className="btn-ghost !border-fendi/20 !py-3 !px-8 text-[11px] uppercase tracking-widest">Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={remove} className="bg-destructive text-white hover:bg-destructive/90 !py-3 !px-8 text-[11px] uppercase tracking-widest rounded-none">
              Confirmar Exclusão
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
